import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import { JobSection, CustomerSection } from "@/components/booking-form/JobCustomerSections";
import { MoveSection, DetailsSection } from "@/components/booking-form/MoveSection";
import { PricingSection, OperationsSection } from "@/components/booking-form/PricingOpsSections";
import { AiAutofill } from "@/components/booking-form/AiAutofill";
import { api, formatApiError, fieldErrorsFromApi } from "@/lib/api";
import { EMPTY_BOOKING, bookingToForm, formToPayload, validateBooking, computeTotals } from "@/lib/booking";
import { useSettings } from "@/context/SettingsContext";
import { money, fmtDate } from "@/lib/format";

const SECTIONS = [["section-job", "Job"], ["section-customer", "Customer"], ["section-move", "Move"], ["section-details", "Details & notes"], ["section-pricing", "Pricing"], ["section-operations", "Operations"]];

const duplicateOf = (b) => ({ ...bookingToForm(b), status: "Confirmed", deposit_amount: "", deposit_method: "", driver_extras: [] });

export default function BookingFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [originalNo, setOriginalNo] = useState(null);
  const [saving, setSaving] = useState(false);
  const dupId = params.get("duplicate");
  const draftKey = `booking-draft:${id || dupId || "new"}`;
  const [base, setBase] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        let loaded, no = null, stamp = "";
        if (id) {
          const { data } = await api.get(`/bookings/${id}`);
          no = data.job_number; stamp = data.updated_at || ""; loaded = bookingToForm(data);
        } else if (dupId) loaded = duplicateOf((await api.get(`/bookings/${dupId}`)).data);
        else loaded = { ...EMPTY_BOOKING, moving_date: params.get("date") || "" };
        setOriginalNo(no);
        setBase({ form: loaded, stamp });
        const raw = sessionStorage.getItem(draftKey);
        const draft = raw ? JSON.parse(raw) : null;
        if (draft && draft.__stamp === stamp) { delete draft.__stamp; setForm(draft); }
        else { sessionStorage.removeItem(draftKey); setForm(loaded); }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    load();
  }, [id, dupId, params, draftKey]);

  useEffect(() => {
    if (!form || !base) return;
    if (JSON.stringify(form) === JSON.stringify(base.form)) sessionStorage.removeItem(draftKey);
    else sessionStorage.setItem(draftKey, JSON.stringify({ ...form, __stamp: base.stamp }));
  }, [form, base, draftKey]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateBooking(form);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fix the highlighted fields");
      document.getElementById(Object.keys(errs)[0])?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      const { data } = id ? await api.put(`/bookings/${id}`, payload) : await api.post("/bookings", payload);
      toast.success(id ? `Booking ${data.job_number} updated` : `Booking ${data.job_number} created`, {
        action: { label: `Jobs on ${fmtDate(data.moving_date, "d MMM")}`, onClick: () => navigate(`/bookings?date=${data.moving_date}`) }, duration: 8000 });
      sessionStorage.removeItem(draftKey);
      if (id && (window.history.state?.idx ?? 0) > 0) navigate(-1);
      else navigate(`/bookings/${data.id}`, { replace: true });
    } catch (err) {
      setErrors(fieldErrorsFromApi(err));
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="py-20 text-center text-slate-500"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const totals = computeTotals(form);
  const props = { form, set, errors, settings };

  return (
    <form onSubmit={submit} noValidate data-testid="booking-form">
      <PageHeader title={id ? `Edit booking ${originalNo}` : dupId ? "New booking (duplicated)" : "New booking"} subtitle="Fields marked * are required. Every change is recorded in the edit history." />
      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="min-w-0 space-y-6">
          {!id && <AiAutofill form={form} setForm={setForm} settings={settings} />}
          <JobSection {...props} excludeId={id} />
          <CustomerSection {...props} />
          <MoveSection {...props} />
          <DetailsSection {...props} />
          <PricingSection {...props} />
          <OperationsSection {...props} />
        </div>
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <nav className="mb-5 hidden flex-col gap-1 lg:flex">
              {SECTIONS.map(([sid, label]) => (
                <a key={sid} href={`#${sid}`} className="rounded px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900">{label}</a>
              ))}
            </nav>
            <dl className="mb-5 space-y-2 border-t border-slate-100 pt-4 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Driver extras</dt><dd className="font-mono">{money(totals.extras)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Total price</dt><dd className="font-mono font-semibold">{money(totals.total)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Deposit paid</dt><dd className="font-mono">{money(totals.paid)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Outstanding</dt><dd className="font-mono font-semibold text-amber-700">{money(totals.remaining)}</dd></div>
            </dl>
            <Button type="submit" disabled={saving} data-testid="btn-save-booking" className="w-full bg-[#0E1E38] hover:bg-[#162A4D]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {id ? "Save changes" : "Save booking"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => { sessionStorage.removeItem(draftKey); navigate(-1); }} data-testid="btn-cancel-form" className="mt-2 w-full">Cancel</Button>
          </div>
        </aside>
      </div>
    </form>
  );
}
